import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { convertBookingPriceFromUsd } from '@/hotdesk-ui/helpers/booking-filter-price';
import { Currency } from 'hotdesk_typescript';

export type BookingFilterPlan = 'hour' | 'day' | 'month' | 'annual';

export type BookingPlanUsdBounds = Record<
    BookingFilterPlan,
    { min: number; max: number }
>;

const PLAN_PRICE_REMOTE_KEYS: Record<
    BookingFilterPlan,
    { min: RemoteConfigKeys; max: RemoteConfigKeys }
> = {
    hour: {
        min: RemoteConfigKeys.WEB_HOUR_FILTER_PRICE_MIN,
        max: RemoteConfigKeys.WEB_HOUR_FILTER_PRICE_MAX
    },
    day: {
        min: RemoteConfigKeys.WEB_DAY_FILTER_PRICE_MIN,
        max: RemoteConfigKeys.WEB_DAY_FILTER_PRICE_MAX
    },
    month: {
        min: RemoteConfigKeys.WEB_MONTH_FILTER_PRICE_MIN,
        max: RemoteConfigKeys.WEB_MONTH_FILTER_PRICE_MAX
    },
    annual: {
        min: RemoteConfigKeys.WEB_ANNUAL_FILTER_PRICE_MIN,
        max: RemoteConfigKeys.WEB_ANNUAL_FILTER_PRICE_MAX
    }
};

/** USD fallbacks aligned with PlanClient initial state when Remote Config is unavailable. */
export const BOOKING_PLAN_PRICE_USD_FALLBACKS: BookingPlanUsdBounds = {
    hour: { min: 0, max: 2000 },
    day: { min: 0, max: 20000 },
    month: { min: 0, max: 100000 },
    annual: { min: 0, max: 200000 }
};

/** Matches Explore `FiltersContext` default `priceLte` before a plan is selected. */
export const DEFAULT_FILTER_PRICE_LTE = 100000;

/** Used by Explore clear-all fallback; booking uses {@link DEFAULT_FILTER_PRICE_LTE} for slider max. */
export const DEFAULT_FILTER_PRICE_CEIL = 5000;

function normalizeUsdBound(value: number, fallback: number): number {
    return Number.isFinite(value) ? value : fallback;
}

/** Preloads all plan min/max bounds from Firebase Remote Config (same keys as PlanClient). */
export async function loadAllBookingPlanPriceBoundsFromRemoteConfig(): Promise<BookingPlanUsdBounds> {
    try {
        const plans = Object.keys(
            PLAN_PRICE_REMOTE_KEYS
        ) as BookingFilterPlan[];
        const entries = await Promise.all(
            plans.map(async (plan) => {
                const keys = PLAN_PRICE_REMOTE_KEYS[plan];
                const fallback = BOOKING_PLAN_PRICE_USD_FALLBACKS[plan];
                const [min, max] = await Promise.all([
                    fetchRemoteConfig(keys.min, 'number') as Promise<number>,
                    fetchRemoteConfig(keys.max, 'number') as Promise<number>
                ]);
                return [
                    plan,
                    {
                        min: normalizeUsdBound(min, fallback.min),
                        max: normalizeUsdBound(max, fallback.max)
                    }
                ] as const;
            })
        );
        return Object.fromEntries(entries) as BookingPlanUsdBounds;
    } catch (error) {
        console.error(error);
        return { ...BOOKING_PLAN_PRICE_USD_FALLBACKS };
    }
}

export function getDisplayPriceBoundsForPlan(
    plan: BookingFilterPlan,
    usdBounds: BookingPlanUsdBounds,
    currencyList: Currency[],
    displayCurrencyCode: string
): { min: number; max: number } {
    const { min: minUsd, max: maxUsd } = usdBounds[plan];
    return {
        min: convertBookingPriceFromUsd(
            currencyList,
            displayCurrencyCode,
            minUsd
        ),
        max: convertBookingPriceFromUsd(
            currencyList,
            displayCurrencyCode,
            maxUsd
        )
    };
}

export async function getBookingFilterPriceBounds(
    plan: BookingFilterPlan,
    options?: {
        currencyList?: Currency[];
        displayCurrencyCode?: string;
    }
): Promise<{ min: number; max: number }> {
    const usdBounds = await loadAllBookingPlanPriceBoundsFromRemoteConfig();
    const displayCurrencyCode = options?.displayCurrencyCode ?? 'USD';
    const currencyList = options?.currencyList ?? [];
    return getDisplayPriceBoundsForPlan(
        plan,
        usdBounds,
        currencyList,
        displayCurrencyCode
    );
}

/** @deprecated Use DEFAULT_FILTER_PRICE_LTE */
export const DEFAULT_BOOKING_FILTER_PRICE_CEIL = DEFAULT_FILTER_PRICE_LTE;
